import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Pill } from "../../shared/ui";
import { delay, stagger } from "../../shared/motion";
import { GRID, loc, type GridCard, type Lang } from "./mock";

interface GridPhoneProps {
  /** Moves the whole phone on scroll (parallax). */
  phoneRef: RefObject<HTMLDivElement>;
  /** Scrolls the card list inside the screen. */
  listRef: RefObject<HTMLDivElement>;
  /** The list's viewport — the scroll distance is list height minus this. */
  wrapRef: RefObject<HTMLDivElement>;
}

// Status-bar glyphs, verbatim from the prototype.
function StatusIcons() {
  return (
    <i>
      <svg width="18" height="12" viewBox="0 0 18 12" fill="currentColor">
        <rect x="0" y="7" width="3" height="5" rx="1" />
        <rect x="5" y="4.5" width="3" height="7.5" rx="1" />
        <rect x="10" y="2" width="3" height="10" rx="1" />
        <rect x="15" y="0" width="3" height="12" rx="1" />
      </svg>
      <svg width="16" height="12" viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M1.5 4.4a10 10 0 0 1 13 0" />
        <path d="M4 7.2a6.3 6.3 0 0 1 8 0" />
        <circle cx="8" cy="10.2" r="1.4" fill="currentColor" stroke="none" />
      </svg>
      <svg width="25" height="12" viewBox="0 0 25 12" fill="none">
        <rect x="0.5" y="0.5" width="21" height="11" rx="3.5" stroke="currentColor" opacity=".4" />
        <rect x="2" y="2" width="18" height="8" rx="2.2" fill="currentColor" />
      </svg>
    </i>
  );
}

function GridCardView({ c, i, lang }: { c: GridCard; i: number; lang: Lang }) {
  const { t } = useTranslation();
  const red = c.tone === "overdue";
  const barTone = red ? " red" : c.tone === "closed" ? " blue" : "";

  let footer;
  if (c.tone === "overdue") {
    footer = (
      <>
        <div className="l red">{t("hero.mock.days_late", { n: c.late })}</div>
        <div className="v red">{c.due}</div>
      </>
    );
  } else if (c.tone === "pending") {
    footer = (
      <>
        <div className="l">{t("hero.mock.awaiting_sign")}</div>
        <div className="v lnk">{t("hero.mock.resend")}</div>
      </>
    );
  } else if (c.tone === "draft") {
    footer = <div className="v lnk">{t("hero.mock.resume")} ›</div>;
  } else {
    footer = (
      <>
        <div className="l">{t(c.tone === "closed" ? "hero.mock.fully_paid" : "hero.mock.next_payment")}</div>
        <div className="v">{c.due}</div>
      </>
    );
  }

  return (
    <div className={red ? "gc red" : "gc"} style={stagger(i)}>
      <div className="gc-top">
        <Pill tone={c.tone} sm>
          {t(`hero.mock.status_${c.tone}`)}
        </Pill>
        <Icon name="i-more" />
      </div>
      <div>
        <div className="gc-nm">{c.name}</div>
        <div className="gc-it">
          <Icon name={c.icon} />
          {loc(c.item, lang)}
        </div>
      </div>
      <div className="gc-mid">
        {c.tone === "draft" ? (
          <div className="drafts">
            {Array.from({ length: c.m }, (_, k) => (
              <i key={k} className={k < c.n ? "f" : undefined} />
            ))}
          </div>
        ) : (
          <>
            <div>
              <b>{c.paid}</b>
              <small>/ {c.total}</small>
            </div>
            <div className={"bar sm" + barTone}>
              <span style={{ width: `${c.pct}%` }} />
            </div>
          </>
        )}
        <span className="gc-n">
          {t(c.tone === "draft" ? "hero.mock.stages" : "hero.mock.months", { a: c.n, b: c.m })}
        </span>
      </div>
      <div className="gc-ft">{footer}</div>
    </div>
  );
}

// The merchant app's «Shartnomalar» screen as a static phone mock (prototype 141–167). Decorative:
// the whole phone is aria-hidden; its texts are still translated so the screenshot reads right.
export function GridPhone({ phoneRef, listRef, wrapRef }: GridPhoneProps) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === "ru" ? "ru" : "uz";

  return (
    <div className="phone-wrap" data-r="up" style={delay(320)} aria-hidden="true">
      <div className="par-phone" ref={phoneRef}>
        <div className="phone">
          <div className="scr">
            <div className="island" />
            <div className="a-hdr">
              <div className="sbar">
                <span>9:41</span>
                <StatusIcons />
              </div>
              <div className="t-row">
                <h4>{t("hero.mock.title")}</h4>
                <span className="rbtn">
                  <Icon name="i-rows" />
                </span>
                <span className="pillbtn">
                  <Icon name="i-archive" />
                  {t("hero.mock.archive")}
                  <em>6</em>
                </span>
              </div>
              <div className="pseg">
                <span className="on">
                  {t("hero.mock.seg_agreements")}
                  <em>24</em>
                </span>
                <span>
                  {t("hero.mock.seg_clients")}
                  <em>18</em>
                </span>
              </div>
              <div className="srch-row">
                <div className="srch">
                  <Icon name="i-search" />
                  {t("hero.mock.search")}
                </div>
                <span className="ibtn">
                  <Icon name="i-sliders" />
                </span>
                <span className="ibtn">
                  <Icon name="i-sort" />
                </span>
              </div>
              <div className="chips">
                <span className="chip on">
                  {t("hero.mock.chip_all")}
                  <span className="c">24</span>
                </span>
                <span className="chip">
                  {t("hero.mock.chip_overdue")}
                  <span className="c red">3</span>
                </span>
                <span className="chip">
                  {t("hero.mock.chip_active")}
                  <span className="c">15</span>
                </span>
                <span className="chip">
                  {t("hero.mock.chip_pending")}
                  <span className="c">2</span>
                </span>
              </div>
            </div>
            <div className="g-wrap" ref={wrapRef}>
              <div className="g-list" ref={listRef}>
                {GRID.map((c, i) => (
                  <GridCardView key={c.name} c={c} i={i} lang={lang} />
                ))}
              </div>
            </div>
            <div className="g-fade" />
            <span className="btn btn-primary btn-float g-cta">
              <Icon name="i-plus" />
              {t("hero.mock.create")}
            </span>
            <div className="tabbar">
              <span>
                <Icon name="i-home" />
                {t("hero.mock.tab_home")}
              </span>
              <span className="on">
                <Icon name="i-file" />
                {t("hero.mock.tab_agreements")}
              </span>
              <span>
                <Icon name="i-inbox" />
                {t("hero.mock.tab_inbox")}
              </span>
              <span>
                <Icon name="i-wallet" />
                {t("hero.mock.tab_debts")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
